import { useCurrentUserId } from '@components/Common/Utils/roles';
import { useToast } from '@components/Common/Toast/ToastProvider';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import { Box, Button, Typography } from '@mui/material';
import React, { useState } from 'react';

import { requestPushPermission, usePushPermission } from '@helpers/push/pushPermission';
import { isPushPromptSnoozed, shouldShowPushPrompt, snoozePushPrompt } from '@helpers/push/pushPrompt';
import './index.scss';

export const PUSH_PROMPT_COPY =
  'Turn on desktop alerts so you hear about new employers, jobs and subscriptions as they happen';

/**
 * The slim "turn on desktop alerts" strip at the top of every signed-in page (owner, 2026-10-24).
 *
 * Shows only while Chrome has no answer and the person has not said "Not now" in the last seven
 * days (helpers/push/pushPrompt). "Turn on" asks Chrome; whatever Chrome answers, the shared
 * permission store tells the bell and the alerts page, and the strip goes because the permission
 * is no longer 'default'. Which alerts arrive is the person's alert preferences, unchanged.
 */
const PushPrompt: React.FC = () => {
  const userId = useCurrentUserId();
  const permission = usePushPermission();
  const { showToast } = useToast();
  // Read once per person; "Not now" flips it locally so the strip goes without a re-read.
  const [snoozedFor, setSnoozedFor] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);

  const snoozed = !!userId && (snoozedFor === userId || isPushPromptSnoozed(userId));
  if (!shouldShowPushPrompt({ userId, permission, snoozed })) return null;

  const turnOn = async () => {
    setAsking(true);
    const answer = await requestPushPermission();
    setAsking(false);
    if (answer === 'granted') showToast('Desktop alerts are on', 'success');
    else if (answer === 'denied') {
      showToast('Chrome blocked desktop alerts. You can allow them from My alerts.', 'warning');
    }
  };

  const notNow = () => {
    if (!userId) return;
    snoozePushPrompt(userId);
    setSnoozedFor(userId);
  };

  return (
    <Box className="push-prompt" role="region" aria-label="Desktop alerts">
      <NotificationsActiveOutlinedIcon className="push-prompt__icon" aria-hidden="true" />
      <Typography className="push-prompt__text">{PUSH_PROMPT_COPY}</Typography>
      <Box className="push-prompt__actions">
        <Button variant="text" size="small" onClick={notNow}>
          Not now
        </Button>
        <Button variant="contained" size="small" onClick={turnOn} disabled={asking}>
          Turn on
        </Button>
      </Box>
    </Box>
  );
};

export default PushPrompt;
